import React, { useState, useMemo } from 'react';
import { Plus, Edit2, Trash2, X, Shield, Copy, ChevronDown, ChevronRight, Check } from 'lucide-react';
import { toast } from '../utils/toast';
import { MODULOS, NAV_MODULOS } from '../config/modulos';

// ─── Módulos do sistema (derivados do registry único) ────────────────────────
// Para adicionar/editar módulos, EDITE src/config/modulos.js — alterações
// propagam automaticamente para o editor de perfis e o resumo de permissões.

export const MODULOS_SISTEMA = NAV_MODULOS;

// Estrutura completa: módulos, sub-módulos, ações e campos especiais.
// Normaliza defaults (acoes=['ver'], subs=[]) para entradas mínimas no registry.
const ESTRUTURA = MODULOS.map(m => ({
  ...m,
  acoes: m.acoes || ['ver'],
  subs:  m.subs  || [],
}));

const ACOES_LABELS = {
  ver:          'Visualizar',
  criar:        'Criar',
  editarTodos:  'Editar todos',
  excluirTodos: 'Excluir todos',
};

// ─── Funções utilitárias ──────────────────────────────────────────────────────

function permissoesDefault() {
  const p = {};
  ESTRUTURA.forEach(mod => {
    p[mod.id] = {};
    mod.acoes.forEach(a => { p[mod.id][a] = true; });
    if (mod.tipoOpcoes) p[mod.id].tipo = mod.tipoOpcoes[0].value;
    mod.subs.forEach(sub => {
      p[sub.id] = {};
      sub.acoes.forEach(a => { p[sub.id][a] = true; });
    });
  });
  return p;
}

function permissoesMinimasDefault() {
  const p = {};
  ESTRUTURA.forEach(mod => {
    p[mod.id] = {};
    mod.acoes.forEach(a => { p[mod.id][a] = false; });
    if (mod.tipoOpcoes) p[mod.id].tipo = mod.tipoOpcoes[0].value;
    mod.subs.forEach(sub => {
      p[sub.id] = {};
      sub.acoes.forEach(a => { p[sub.id][a] = false; });
    });
  });
  return p;
}

// Compatibilidade: converte perfil antigo (modulos[]) para novo (permissoes{})
function normalizar(p) {
  if (p.permissoes) {
    // Garante que novos módulos existam nas permissões do perfil antigo
    const base = permissoesDefault();
    const merged = { ...base, ...p.permissoes };
    return { ...p, permissoes: merged };
  }
  const perm = permissoesDefault();
  if (Array.isArray(p.modulos)) {
    ESTRUTURA.forEach(mod => {
      if (!p.modulos.includes(mod.id)) {
        Object.keys(perm[mod.id] || {}).forEach(a => { perm[mod.id][a] = false; });
        mod.subs.forEach(sub => {
          Object.keys(perm[sub.id] || {}).forEach(a => { perm[sub.id][a] = false; });
        });
      }
    });
  }
  return { ...p, permissoes: perm };
}

function moduloAtivo(perm, modId) {
  return perm[modId]?.ver !== false;
}

// ─── Perfis padrão NR-1 ───────────────────────────────────────────────────────

function criarPermissoesNR1(tipo) {
  const p = permissoesMinimasDefault();
  p.diagnosticoNR1 = { ver: true, tipo };
  return p;
}

export const PERFIS_NR1_PADRAO = [
  {
    nome: 'Diagnóstico NR1 - RH',
    descricao: 'Acesso à Etapa 1 do Diagnóstico NR-1 (questionário de RH)',
    modulos: ['diagnosticoNR1'],
    permissoes: criarPermissoesNR1('rh'),
    isKzero: false,
  },
  {
    nome: 'Diagnóstico NR1 - Funcionários',
    descricao: 'Acesso à Etapa 2 do Diagnóstico NR-1 (pesquisa com funcionários)',
    modulos: ['diagnosticoNR1'],
    permissoes: criarPermissoesNR1('funcionario'),
    isKzero: false,
  },
];

// ─── Componente: Toggle Switch ────────────────────────────────────────────────

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onChange}
      style={{
        width: 40, height: 22, borderRadius: 11, border: 'none', cursor: disabled ? 'default' : 'pointer',
        background: checked ? '#7c3aed' : '#ebe7f3',
        position: 'relative', transition: 'background 0.2s', flexShrink: 0,
        opacity: disabled ? 0.4 : 1,
      }}
    >
      <span style={{
        position: 'absolute', top: 3, left: checked ? 21 : 3,
        width: 16, height: 16, borderRadius: '50%', background: 'white',
        transition: 'left 0.2s', display: 'block',
      }}/>
    </button>
  );
}

// ─── Componente: Editor de permissões de um módulo ───────────────────────────

function ModuloEditor({ mod, perm, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const ativo = moduloAtivo(perm, mod.id);
  const temSubs = mod.subs.length > 0;
  const tipoAtual = perm[mod.id]?.tipo || (mod.tipoOpcoes?.[0]?.value ?? '');

  const toggleModulo = () => {
    const novoAtivo = !ativo;
    onChange(mod.id, 'ver', novoAtivo);
    if (!novoAtivo) {
      // Desabilita todas as ações e subs
      mod.acoes.filter(a => a !== 'ver').forEach(a => onChange(mod.id, a, false));
      mod.subs.forEach(sub => {
        sub.acoes.forEach(a => onChange(sub.id, a, false));
      });
    } else {
      // Auto-expande ao ativar
      setExpanded(true);
    }
  };

  const habilitarTudoSub = () => {
    mod.acoes.forEach(a => onChange(mod.id, a, true));
    mod.subs.forEach(sub => sub.acoes.forEach(a => onChange(sub.id, a, true)));
  };

  const desabilitarSubs = () => {
    mod.acoes.filter(a => a !== 'ver').forEach(a => onChange(mod.id, a, false));
    mod.subs.forEach(sub => sub.acoes.forEach(a => onChange(sub.id, a, false)));
  };

  return (
    <div style={{
      border: `1px solid ${ativo ? 'rgba(124,58,237,0.35)' : '#eeeaf5'}`,
      borderRadius: 10, overflow: 'hidden',
      background: ativo ? 'rgba(124,58,237,0.04)' : '#ffffff',
      transition: 'border-color 0.2s',
    }}>
      {/* Cabeçalho do módulo */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', gap: 12 }}>
        {/* Toggle principal */}
        <Toggle checked={ativo} onChange={toggleModulo} />

        {/* Emoji + Nome */}
        <span style={{ fontSize: 18, lineHeight: 1 }}>{mod.emoji}</span>
        <span style={{ fontWeight: 700, color: ativo ? '#1c1530' : '#4b5563', fontSize: 14, flex: 1 }}>
          {mod.label}
        </span>

        {/* Resumo quando ativo e não expandido */}
        {ativo && !expanded && (
          <span style={{ fontSize: 11, color: '#64748b' }}>
            {mod.acoes.filter(a => a !== 'ver' && perm[mod.id]?.[a] !== false).length} ação(ões) · {mod.subs.filter(s => perm[s.id]?.ver !== false).length}/{mod.subs.length} sub
          </span>
        )}

        {/* Botão expandir/colapsar (quando ativo e tem configurações) */}
        {ativo && (temSubs || mod.tipoOpcoes || mod.acoes.length > 1) && (
          <button
            onClick={() => setExpanded(!expanded)}
            style={{ background: 'none', border: 'none', color: '#5f5874', cursor: 'pointer', display: 'flex', padding: 2 }}
          >
            {expanded ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
          </button>
        )}
      </div>

      {/* Detalhes — visível quando módulo ativo e expandido */}
      {ativo && expanded && (
        <div style={{ borderTop: '1px solid #eeeaf5', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Ações do módulo pai (exceto 'ver' que é controlado pelo toggle) */}
          {mod.acoes.filter(a => a !== 'ver').length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#5f5874', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ações do módulo</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={habilitarTudoSub} style={s.linkBtn}>Tudo ✓</button>
                  <button onClick={desabilitarSubs} style={{ ...s.linkBtn, color: '#ef4444' }}>Tudo ✗</button>
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {mod.acoes.filter(a => a !== 'ver').map(a => {
                  const ligado = perm[mod.id]?.[a] !== false;
                  return (
                    <label key={a} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input type="checkbox" checked={ligado}
                        onChange={() => onChange(mod.id, a, !ligado)}
                        style={{ accentColor: '#7c3aed', width: 14, height: 14 }}
                      />
                      <span style={{ fontSize: 12, color: ligado ? '#2d2640' : '#4b5563' }}>{ACOES_LABELS[a]}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Campo especial de tipo (NR-1) */}
          {mod.tipoOpcoes && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#5f5874', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Nível de acesso</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {mod.tipoOpcoes.map(op => {
                  const sel = tipoAtual === op.value;
                  return (
                    <button key={op.value} onClick={() => onChange(mod.id, 'tipo', op.value)}
                      style={{
                        padding: '6px 14px', fontSize: 12, borderRadius: 20, cursor: 'pointer', fontWeight: sel ? 600 : 400,
                        border: `1px solid ${sel ? '#7c3aed' : '#e5e0ef'}`,
                        background: sel ? 'rgba(124,58,237,0.2)' : 'transparent',
                        color: sel ? '#6d28d9' : '#5f5874',
                      }}
                    >
                      {sel && <Check size={10} style={{ marginRight: 4, display: 'inline' }}/>}
                      {op.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sub-módulos */}
          {temSubs && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#5f5874', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sub-módulos</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {mod.subs.map(sub => {
                  const subAtivo = perm[sub.id]?.ver !== false;
                  return (
                    <div key={sub.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                      background: subAtivo ? 'rgba(124,58,237,0.06)' : 'transparent',
                      borderRadius: 7, border: `1px solid ${subAtivo ? 'rgba(124,58,237,0.15)' : '#eeeaf5'}`,
                    }}>
                      {/* Toggle sub */}
                      <Toggle checked={subAtivo} onChange={() => {
                        const novo = !subAtivo;
                        sub.acoes.forEach(a => onChange(sub.id, a, novo));
                        if (novo) onChange(mod.id, 'ver', true);
                      }} />
                      <span style={{ fontSize: 13, color: subAtivo ? '#2d2640' : '#4b5563', flex: 1 }}>{sub.label}</span>
                      {/* Ações do sub (exceto ver) */}
                      {subAtivo && sub.acoes.filter(a => a !== 'ver').length > 0 && (
                        <div style={{ display: 'flex', gap: 12 }}>
                          {sub.acoes.filter(a => a !== 'ver').map(a => {
                            const ligado = perm[sub.id]?.[a] !== false;
                            return (
                              <label key={a} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                                <input type="checkbox" checked={ligado}
                                  onChange={() => onChange(sub.id, a, !ligado)}
                                  style={{ accentColor: '#7c3aed', width: 13, height: 13 }}
                                />
                                <span style={{ fontSize: 11, color: ligado ? '#5f5874' : '#4b5563' }}>{ACOES_LABELS[a]}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Componente: Card de perfil na lista ─────────────────────────────────────

function PerfilCard({ perfil, onEditar, onDuplicar, onExcluir, selecionado, onClick }) {
  const norm = normalizar(perfil);
  const modulosAtivos = ESTRUTURA.filter(m => norm.permissoes[m.id]?.ver !== false);
  const tipoNR1 = norm.permissoes['diagnosticoNR1']?.tipo;
  const nr1Ativo = norm.permissoes['diagnosticoNR1']?.ver !== false;

  return (
    <div
      onClick={onClick}
      style={{
        background: selecionado ? 'rgba(124,58,237,0.1)' : '#ffffff',
        border: `1px solid ${selecionado ? '#7c3aed' : '#e5e0ef'}`,
        borderRadius: 10, padding: '14px 16px',
        cursor: 'pointer', transition: 'border-color 0.15s',
        display: 'flex', flexDirection: 'column', gap: 10,
      }}
    >
      {/* Nome + ações */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Shield size={13} color={selecionado ? '#6d28d9' : '#8b5cf6'}/>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#1c1530', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{perfil.nome}</span>
            {perfil.isKzero === false && (
              <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: 'rgba(56,139,253,0.12)', color: '#1d4ed8', fontWeight: 600, flexShrink: 0 }}>Cliente</span>
            )}
          </div>
          {perfil.descricao && (
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{perfil.descricao}</div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 2, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          <button title="Editar" onClick={onEditar} style={s.iconBtn('#8b5cf6')}><Edit2 size={13}/></button>
          <button title="Duplicar" onClick={onDuplicar} style={s.iconBtn('#64748b')}><Copy size={13}/></button>
          <button title="Excluir" onClick={onExcluir} style={s.iconBtn('#ef4444')}><Trash2 size={13}/></button>
        </div>
      </div>

      {/* Módulos ativos */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {modulosAtivos.length === 0 ? (
          <span style={{ fontSize: 11, color: '#4b5563', fontStyle: 'italic' }}>Nenhum módulo ativo</span>
        ) : (
          modulosAtivos.map(m => (
            <span key={m.id} style={{
              fontSize: 11, padding: '2px 9px', borderRadius: 20,
              background: 'rgba(124,58,237,0.12)', color: '#6d28d9',
              border: '1px solid rgba(124,58,237,0.25)', fontWeight: 500,
            }}>
              {m.emoji} {m.label}
              {m.id === 'diagnosticoNR1' && nr1Ativo && tipoNR1 && (
                <span style={{ color: '#64748b', marginLeft: 4 }}>· {tipoNR1}</span>
              )}
            </span>
          ))
        )}
        {modulosAtivos.length === ESTRUTURA.length && (
          <span style={{ fontSize: 10, color: '#10b981', fontWeight: 600 }}>✓ Acesso total</span>
        )}
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export function PerfisAcesso({ perfisAcesso, setPerfisAcesso, onAplicarPerfis }) {
  const [perfilSelecionado, setPerfilSelecionado] = useState(null); // id do perfil em edição
  const [form, setForm] = useState(null); // { nome, descricao, permissoes }
  const [isNovo, setIsNovo] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('kzero'); // 'kzero' | 'cliente' | 'todos'

  const perfisFiltrados = useMemo(() =>
    perfisAcesso.filter(p => {
      if (busca && !p.nome.toLowerCase().includes(busca.toLowerCase())) return false;
      if (filtroTipo === 'kzero') return p.isKzero !== false; // default true para retrocompatibilidade
      if (filtroTipo === 'cliente') return p.isKzero === false;
      return true;
    }),
    [perfisAcesso, busca, filtroTipo]
  );

  const abrirNovo = () => {
    const f = { nome: '', descricao: '', permissoes: permissoesMinimasDefault(), isKzero: true };
    setForm(f);
    setPerfilSelecionado(null);
    setIsNovo(true);
  };

  const abrirEditar = (p) => {
    const norm = normalizar(p);
    setForm({ nome: norm.nome, descricao: norm.descricao || '', permissoes: JSON.parse(JSON.stringify(norm.permissoes)), isKzero: p.isKzero !== false });
    setPerfilSelecionado(p.id);
    setIsNovo(false);
  };

  const duplicar = (p) => {
    const norm = normalizar(p);
    const novo = {
      ...norm,
      id: Date.now(),
      nome: `Cópia de ${norm.nome}`,
    };
    setPerfisAcesso([...perfisAcesso, novo]);
    toast.sucesso('Perfil duplicado!');
    abrirEditar(novo);
  };

  const excluir = (id) => {
    if (!window.confirm('Excluir este perfil de acesso?')) return;
    setPerfisAcesso(perfisAcesso.filter(p => p.id !== id));
    if (perfilSelecionado === id) { setForm(null); setPerfilSelecionado(null); }
    toast.sucesso('Perfil excluído.');
  };

  const salvar = () => {
    if (!form?.nome?.trim()) { toast.erro('Informe um nome para o perfil.'); return; }
    const modulos = ESTRUTURA.filter(m => form.permissoes[m.id]?.ver !== false).map(m => m.id);
    if (isNovo) {
      const novo = { ...form, modulos, isKzero: form.isKzero !== false, id: Date.now() };
      setPerfisAcesso([...perfisAcesso, novo]);
      setPerfilSelecionado(novo.id);
      setIsNovo(false);
      toast.sucesso('Perfil criado!');
    } else {
      setPerfisAcesso(perfisAcesso.map(p => p.id === perfilSelecionado ? { ...p, ...form, modulos, isKzero: form.isKzero !== false } : p));
      toast.sucesso('Perfil atualizado!');
    }
  };

  const cancelar = () => { setForm(null); setPerfilSelecionado(null); setIsNovo(false); };

  const onChangePerm = (entidade, acao, valor) => {
    setForm(f => {
      const np = JSON.parse(JSON.stringify(f.permissoes));
      if (!np[entidade]) np[entidade] = {};
      np[entidade][acao] = valor;
      // Se habilitar algo num sub, garante ver no pai
      if (valor && acao !== 'tipo') {
        const mod = ESTRUTURA.find(m => m.subs.some(sub => sub.id === entidade));
        if (mod) { np[mod.id] = np[mod.id] || {}; np[mod.id].ver = true; }
      }
      return { ...f, permissoes: np };
    });
  };

  const criarPerfisNR1 = () => {
    const novos = PERFIS_NR1_PADRAO
      .filter(p => !perfisAcesso.some(ex => ex.nome === p.nome))
      .map(p => ({ ...p, id: Date.now() + Math.random() }));
    if (novos.length === 0) { toast.aviso('Perfis NR-1 já existem.'); return; }
    setPerfisAcesso([...perfisAcesso, ...novos]);
    toast.sucesso(`${novos.length} perfil(is) NR-1 criado(s)!`);
  };

  const presetAcessoTotal = () => {
    setForm(f => ({ ...f, permissoes: permissoesDefault() }));
    toast.info('Acesso total aplicado. Salve para confirmar.');
  };

  const presetAcessoMinimo = () => {
    setForm(f => ({ ...f, permissoes: permissoesMinimasDefault() }));
    toast.info('Acesso mínimo aplicado. Salve para confirmar.');
  };

  const modulosAtivosNoForm = form ? ESTRUTURA.filter(m => form.permissoes[m.id]?.ver !== false).length : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1c1530', margin: 0 }}>Perfis de Acesso</h2>
          <p style={{ fontSize: 12, color: '#64748b', margin: '3px 0 0' }}>Gerencie grupos de permissões e atribua aos usuários</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {onAplicarPerfis && (
            <button style={{ ...s.btn, background: 'rgba(16,185,129,0.1)', color: '#10b981', border: '1px solid rgba(16,185,129,0.25)' }}
              onClick={async () => { await onAplicarPerfis(); toast.sucesso('Permissões aplicadas ao sistema!'); }}>
              🔄 Aplicar ao Sistema
            </button>
          )}
          <button style={{ ...s.btn, background: 'rgba(56,139,253,0.1)', color: '#1d4ed8', border: '1px solid rgba(56,139,253,0.25)' }}
            onClick={criarPerfisNR1} title="Cria perfis padrão para Diagnóstico NR-1">
            🔍 Perfis NR-1
          </button>
          <button style={s.btnPrimary} onClick={abrirNovo}>
            <Plus size={14}/> Novo Perfil
          </button>
        </div>
      </div>

      {/* Aviso */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 8, padding: '8px 12px', fontSize: 11, color: '#f59e0b', marginBottom: 20 }}>
        <Shield size={12}/>
        <span>Usuários sempre podem editar e excluir os próprios registros, independente das permissões do perfil.</span>
      </div>

      {/* Layout split */}
      <div style={{ display: 'grid', gridTemplateColumns: form ? '340px 1fr' : '1fr', gap: 20, alignItems: 'start' }}>

        {/* Painel esquerdo — lista de perfis */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Filtro Kzero / Cliente / Todos */}
          <div style={{ display: 'flex', gap: 4, background: '#ffffff', borderRadius: 8, padding: 3, border: '1px solid #eeeaf5' }}>
            {[
              { key: 'kzero', label: 'Kzero' },
              { key: 'cliente', label: 'Clientes' },
              { key: 'todos', label: 'Todos' },
            ].map(t => (
              <button key={t.key} onClick={() => setFiltroTipo(t.key)} style={{
                flex: 1, padding: '5px 8px', fontSize: 11, fontWeight: 600, borderRadius: 6,
                border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                background: filtroTipo === t.key ? '#7c3aed' : 'transparent',
                color: filtroTipo === t.key ? '#fff' : '#5f5874',
              }}>{t.label}</button>
            ))}
          </div>

          {/* Busca */}
          <input
            placeholder="🔎 Buscar perfil..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
            style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, color: '#1c1530', padding: '8px 12px', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' }}
          />

          {/* Contador */}
          <div style={{ fontSize: 11, color: '#64748b' }}>
            {perfisFiltrados.length} perfil(is) · {perfisAcesso.length} total
          </div>

          {/* Lista */}
          {perfisFiltrados.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#4b5563' }}>
              <Shield size={32} color="#eeeaf5" style={{ marginBottom: 8 }}/>
              <div style={{ fontSize: 13 }}>{busca ? 'Nenhum perfil encontrado.' : 'Nenhum perfil criado.'}</div>
              {!busca && <div style={{ fontSize: 12, marginTop: 4 }}>Clique em "+ Novo Perfil" para começar.</div>}
            </div>
          ) : (
            perfisFiltrados.map(p => (
              <PerfilCard
                key={p.id}
                perfil={p}
                selecionado={perfilSelecionado === p.id || (isNovo && false)}
                onClick={() => abrirEditar(p)}
                onEditar={() => abrirEditar(p)}
                onDuplicar={() => duplicar(p)}
                onExcluir={() => excluir(p.id)}
              />
            ))
          )}
        </div>

        {/* Painel direito — editor */}
        {form && (
          <div style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 12, padding: '20px', display: 'flex', flexDirection: 'column', gap: 20, position: 'sticky', top: 16 }}>
            {/* Cabeçalho do editor */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#1c1530' }}>
                  {isNovo ? '🛡 Novo Perfil' : `✏️ Editando: ${form.nome || '...'}`}
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>
                  {modulosAtivosNoForm} de {ESTRUTURA.length} módulos ativos
                </div>
              </div>
              <button onClick={cancelar} style={{ background: 'none', border: 'none', color: '#5f5874', cursor: 'pointer', padding: 4 }}>
                <X size={18}/>
              </button>
            </div>

            {/* Nome e descrição */}
            <div className="form-grid-2" style={{ gap: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label style={s.label}>Nome do Perfil *</label>
                <input style={s.input}
                  placeholder="Ex: Consultor, Aluno, Admin..."
                  value={form.nome}
                  onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label style={s.label}>Descrição</label>
                <input style={s.input}
                  placeholder="Breve descrição do perfil"
                  value={form.descricao}
                  onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
                />
              </div>
            </div>

            {/* Checkbox Kzero */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox"
                checked={form.isKzero !== false}
                onChange={e => setForm(f => ({ ...f, isKzero: e.target.checked }))}
                style={{ accentColor: '#7c3aed', width: 15, height: 15 }}
              />
              <span style={{ fontSize: 12, color: '#1c1530', fontWeight: 500 }}>Perfil interno da Kzero</span>
              <span style={{ fontSize: 10, color: '#64748b' }}>(desmarque para perfis de clientes)</span>
            </label>

            {/* Presets rápidos */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Presets:</span>
              <button onClick={presetAcessoTotal} style={{ ...s.btn, fontSize: 11, padding: '4px 12px', background: 'rgba(16,185,129,0.1)', color: '#10b981', border: '1px solid rgba(16,185,129,0.2)' }}>
                ✓ Acesso Total
              </button>
              <button onClick={presetAcessoMinimo} style={{ ...s.btn, fontSize: 11, padding: '4px 12px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)' }}>
                ✗ Sem Acesso
              </button>
            </div>

            {/* Módulos */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#5f5874', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Módulos e Permissões
              </div>
              {ESTRUTURA.map(mod => (
                <ModuloEditor key={mod.id} mod={mod} perm={form.permissoes} onChange={onChangePerm} />
              ))}
            </div>

            {/* Ações */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 8, borderTop: '1px solid #eeeaf5' }}>
              <button onClick={cancelar} style={s.btnCancelar}>Cancelar</button>
              <button onClick={salvar} style={s.btnPrimary}>
                {isNovo ? '+ Criar Perfil' : '✓ Salvar Alterações'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Estilos ──────────────────────────────────────────────────────────────────

const s = {
  btn:        { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none' },
  btnPrimary: { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#7c3aed', color: 'white', border: 'none', borderRadius: 20, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnCancelar:{ background: 'transparent', border: '1px solid #e5e0ef', borderRadius: 8, color: '#5f5874', cursor: 'pointer', padding: '8px 18px', fontSize: 13 },
  iconBtn:    (color) => ({ background: 'transparent', border: 'none', color, cursor: 'pointer', padding: '4px 5px', display: 'flex', borderRadius: 5, opacity: 0.7, transition: 'opacity 0.15s' }),
  label:      { fontSize: 11, fontWeight: 600, color: '#5f5874', textTransform: 'uppercase', letterSpacing: '0.05em' },
  input:      { background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, color: '#1c1530', padding: '8px 12px', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' },
  linkBtn:    { background: 'transparent', border: 'none', color: '#8b5cf6', fontSize: 11, cursor: 'pointer', fontWeight: 600, padding: '2px 4px' },
};
